import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import OperationMixin from "common/framework/OperationMixin";
import virtualConstants from "common/helper/virtualConstants";
import popupUtils from "app/widgets/Popup/popupUtils";
import "common/bindings/value";
import "common/bindings/field";
import "common/bindings/enabled";

var DFDropDownList = BaseWidget(kendo.ui.DropDownList).extend(OperationMixin).extend({

    loader: null,

    options: {
        name: "DFDropDownList",
        keepOptionLabel: false,
        optionLabelKey: "",
        // locale entry field on each item
        localeKeyField: false,

        //Key that should be displayed in read-only mode when normal option label would otherwise be displayed
        readonlyOptionLabelKey: "none",

        // When true, the dropdown will have noneAvailable/someAvailable classes applied
        noRecordsKey: true,

        //Special option for virtual mode
        defaultValueMapper: false,

        //Reset the datasource on hide
        resetOnHide: false,

        //Locale key for filter input
        filterKey: false,

        //Adjusts default height of dropdown
        height: virtualConstants.height,

        messages: {
            noData: locale.get("message.options.none")
        },

        //Locale entry for alternate text that is shown on hover
        titleTextKey: null,

        //When set, the list width will be configured to this number of pixels
        listWidth: null,

        popup: {
            collision: "fit"
        }
    },

    init: function(element, options) {

        if (options.localeKeyField) {
            options.template = function(item) {
                if (item && item[options.localeKeyField]) {
                    return locale.get(item[options.localeKeyField]);
                }
            };
            options.valueTemplate = function(e) {
                return locale.get(e[options.localeKeyField]);
            }
        }

        this._configValueMapper(options);
        this._configOptionLabel(options);

        // if datasource is sent via init and we aren't keeping the option label, we need to cache it so option label is configured properly
        // our keepOptionLabel=false logic depends on data being empty when the data is bound on init
        let dsCache;
        if (options.keepOptionLabel !== true && options.dataSource && options.index === -1) {
            dsCache = options.dataSource;
            delete options.dataSource;
        }

        kendo.ui.DropDownList.fn.init.apply(this, arguments);
        this.list.parent().addClass("DFDropDownListPopup");

        if (dsCache) {
            this.setDataSource(dsCache);
        }

        // needed to render the valueTemplate when keepOptionLabel is off
        if(options.keepOptionLabel !== true && options.index === -1) {
            this.select(-1);
        }

        this._configNoRecords();

        if (this.options.listWidth) {
            this.popup?.element?.width(this.options.listWidth);
        }

        this._configResetOnHide();

        if (this.options.filterKey) {
            this.filterInput.attr("placeholder", locale.get(this.options.filterKey));
        }

        //Mantis #42672: If the list view has a focused item and no value, things get weird
        this.bind("cascade", function() {
            if (!this.options.keepOptionLabel && !this.value()) {
                this.listView.focus(-1);
            }
        });

        // Kendo bug, see _firstVisibleItem fn below for details
        if(!this.options.virtual) {
            this.listView._firstVisibleItem = this._firstVisibleItem.bind(this.listView);
        }

        this.ariaRequired(this.element.is("[required]"));

        this._configTitleText();

        this.bind("open", () => {
            popupUtils.configurePopup(this.wrapper[0], this.popup, virtualConstants.height);
        });
    },

    _listOptions() {
        if(this.options.virtual && !this.options.virtual.itemHeight) {
            //letting kendo calculate this hurts performance
            this.options.virtual.itemHeight = virtualConstants.itemHeight;
        }
        return kendo.ui.DropDownList.fn._listOptions.apply(this, arguments);
    },

    ariaRequired(required) {
        this.wrapper.attr("aria-required", required ? "true" : null);
    },

    _configTitleText: function() {
        if (this.options.titleTextKey) {
            var text = locale.get(this.options.titleTextKey);
            this.wrapper.attr("title", text).attr("aria-label", text);
        }
    },


    /**
     * Ensures that cascading continues to work even when dropdowns are moved out of the DOM.
     * @returns {*}
     * @private
     */
    _parentWidget: function() {
        if (!this._cachedParentWidget) {
            this._cachedParentWidget = kendo.ui.DropDownList.fn._parentWidget.apply(this, arguments);
        }
        return this._cachedParentWidget;
    },

    /**
     * Handle the noRecordsKey option.
     * @private
     */
    _configNoRecords: function() {
        if (this.options.noRecordsKey) {
            //Default to assuming there will be data. This lets autoBind=false be functional
            this.wrapper.addClass("someAvailable");
            var update = () => {
                if (this.listView && this.listView.bound()) {
                    var shouldDisplay = (this.dataSource && this.dataSource.view().length > 0) || this.isDropDownFiltered();
                    this.wrapper.toggleClass("someAvailable", shouldDisplay);
                    this.wrapper.toggleClass("noneAvailable", !shouldDisplay);
                }
                this._updateTabIndex();
            };
            this.bind("dataBound", update);

            //update right away because the datasource can be passed into constructor and first dataBound may have already happened
            update();
        }
    },

    /**
     * Determine if the dropdown has been filtered by the user.
     */
    isDropDownFiltered: function() {
        if (this.listView && this.listView.bound()) {
            return this._state == "filter";
        }
        return false;
    },

    /**
     * Handle the custom "default" option for value mapper.
     * @private
     */
    _configValueMapper: function(options) {
        if (options.defaultValueMapper) {
            var me = this;
            me.initializeFirstOp();
            options.virtual.valueMapper = function(mapOptions) {
                if (mapOptions.value && me.dataSource && me.dataSource.findRemoteIndex) {
                    var find = {};
                    find[me.options.dataValueField] = mapOptions.value;
                    me.dataSource.findRemoteIndex(find)
                        .done(me.ifOp(function(index) {
                            mapOptions.success(index);
                        }))
                        .fail(me.ifOp(function() {
                            mapOptions.success([]);
                        }));
                } else {
                    mapOptions.success([]);
                }
            }
        }
    },

    _configOptionLabel: function(options) {
        var me = this;

        var encode = function(text) {
            return $("<span>").text(text).html();
        };

        var optionTemplate = function() {
            var editable = locale.get(me.options.optionLabelKey);
            var readonly = locale.get(me.options.readonlyOptionLabelKey);
            return "<span><span class='editableOptionLabel'>" + editable + "</span><span class='readonlyOptionLabel'>" + readonly + "</span></span>"
        };

        var defaultValueTemplate = function(item) {
            if (me.options.dataTextField && item[me.options.dataTextField]) {
                return encode(item[me.options.dataTextField]);
            } else if (me.options.dataValueField && item[me.options.dataValueField]) {
                return encode(item[me.options.dataValueField]);
            } else {
                return encode(item);
            }
        };

        if (options.optionLabelKey && !options.keepOptionLabel) {
            var valueTemplate = kendo.template(options.valueTemplate || this.options.valueTemplate || defaultValueTemplate);
            options.valueTemplate = function(item) {
                if (item && (!me.options.dataTextField || item[me.options.dataTextField] || (!item.hasOwnProperty(me.options.dataTextField) && me.options.dataValueField && item[me.options.dataValueField]))) {
                    return valueTemplate(item);
                } else {
                    return optionTemplate();
                }
            };
            options.optionLabelTemplate = optionTemplate;

            //By default, the kendo dropdown will attempt to select the first item on load if a dataSource is predefined.
            //Instead, nothing should be selected automatically.
            if (options.index === null || options.index === undefined) {
                options.index = -1;
            }
        } else if (options.optionLabelKey && options.dataTextField && options.dataValueField) {
            options.optionLabel = {};
            options.optionLabel[options.dataValueField] = null;
            options.optionLabel[options.dataTextField] = locale.get(options.optionLabelKey);
            options.optionLabelTemplate = optionTemplate;
        } else if (options.optionLabelKey) {
            options.optionLabel = locale.get(options.optionLabelKey);
            options.optionLabelTemplate = optionTemplate;
        }

    },

    _needReset: false,
    onHide: function() {
        if (this.options.resetOnHide) {
            this._needReset = this.listView.bound();
        }
    },

    _configResetOnHide: function() {
        this.bind("open", function() {
            if (this._needReset) {
                this.dataSource.read();
            }
        });
    },

    /**
     * Override
     * kendo bug; enter doesn't work on the DropDownList popup after using shift-tab to nav to previous input
     * (https://www.telerik.com/account/support-center/view-ticket/1592594)
     */
    _keydown(e) {
        kendo.ui.DropDownList.fn._keydown.apply(this, arguments);
        if(e.keyCode === 16) {
            clearTimeout(this._typingTimeout);
            delete this._typingTimeout;
        }
    },

    dataItem: function() {
        var res = kendo.ui.DropDownList.fn.dataItem.apply(this, arguments);
        if (this.options.optionLabel && this.options.dataTextField && res[this.options.dataTextField] == this.options.optionLabel[this.options.dataTextField]) {
            return;
        }
        return res;
    },

    isOptionLabelSelected() {
        const index = this.hasOptionLabel() ? 0 : -1;
        return this.select() === index && this.span.find(".editableOptionLabel").length;
    },

    /**
     * Override
     * Return text for the custom option label template
     */
    text(text) {
        if(text === undefined && this.isOptionLabelSelected()) {
            const selector = !this._readonlyCache ? "editableOptionLabel" : "readonlyOptionLabel";
            return this.span.find(`.${selector}`).text();
        }
        return kendo.ui.DropDownList.fn.text.apply(this, arguments);
    },

    value: function(val) {
        if (this.options.virtual && arguments.length) {
            var c_val = this.value();
            if (c_val === "" && val === null) {
                //Mantis #42362: "" === null
            } else if (!val) {
                this.startNewOp();
                this.select(-1);
            } else {
                this.startNewOp();
                //Mantis #37444: This is a workaround for a kendo bug.
                this.listView._range(0);
                return kendo.ui.DropDownList.fn.value.apply(this, arguments);
            }
        } else if (arguments.length == 0 && this.options.optionLabel && this.select() <= 0) {
            return null;
        } else if (arguments.length == 1 && !val && this.options.keepOptionLabel && this.options.optionLabel) {
            //Mantis #42641
            this.select(0);
        } else {
            return kendo.ui.DropDownList.fn.value.apply(this, arguments);
        }
    },

    _assignInstance: function(text, value) {
        //This handles DF-52627. value appears to be the OLD value in the current Kendo code.
        //That doesn't matter for any of kendo's features, but for docfinity's option label enhancement, it wrecks havoc.
        //In particular, if the valueField and textField are identical, that stale value has the side-effect of convincing the value template that there is a value.
        //This means that the value template can't render the option label, because it is getting handed a fully filled out object.
        if(!text && this.options.dataValueField && this.options.dataTextField === this.options.dataValueField) {
            value = text;
        }
        return kendo.ui.DropDownList.fn._assignInstance.call(this, text, value);
    },



    _readonlyCache: false,
    _enableCache: true,

    readonly: function(readonly) {
        this._readonlyCache = (readonly === undefined ? true : readonly);
        this.wrapper.toggleClass("readonly", this._readonlyCache);
        this._editable({
            readonly: this._readonlyCache,
            disable: !this._enableCache
        });
        this._updateTabIndex();
    },

    enable: function(enable) {
        this._enableCache = (enable === undefined ? true : enable);
        this._editable({
            readonly: this._readonlyCache,
            disable: !this._enableCache
        });
        this._updateTabIndex();
    },

    /**
     * The core kendo dropdown manages the tabindex in _editable
     * Since we are overriding readonly and enable, we need to ensure the control doesn't receive focus when it shouldn't
     * @private
     */
    _updateTabIndex: function() {
        // dropdown that doesn't have data, is readonly, or is disabled shouldn't be focusable
        if (this._readonlyCache === true || this._enableCache === false) {
            this.disableTabIndex();
        } else {
            this.enableTabIndex();
        }
    },

    /**
     * Override
     * Kendo bug triggers cascade twice in VirtualList
     * This override fails to trigger cascade if the remote index is not found or the request fails
     * https://feedback.telerik.com/kendo-jquery-ui/1480254-cascade-event-is-fired-twice-when-virtualization-is-enabled-and-value-method-is-used
     */
    _listChange(e) {
        const event = e && e.hasOwnProperty("added") && e.hasOwnProperty("removed");
        const skip = event && e.added.length === 0 && e.removed.length > 0;
        if(!this.options.virtual || !skip) {
            kendo.ui.DropDownList.fn._listChange.apply(this, arguments);
        }
    },

    /**
     * Override
     * Kendo bug throws error when filtering a grouped datasource when list is scrolled and filter returns no results
     * https://www.telerik.com/account/support-center/view-ticket/1613100
     */
    _firstVisibleItem() {
        // "this" context is DFDropDownList-instance.listView
        if(this.element[0].children.length === 0) {
            return;
        }
        return kendo.ui.StaticList.fn._firstVisibleItem.apply(this, arguments);
    },

    /**
     * Remove the tab index from the wrapper so the control is not focusable
     */
    disableTabIndex: function() {
        this.wrapper.removeAttr("tabindex");
    },

    /**
     * Enable tab index from the wrapper so the control is focusable
     */
    enableTabIndex: function() {
        this.wrapper.attr("tabindex", this.wrapper.data("tabindex"));
    },

    destroy: function() {
        if (!this._markWidgetAsDestroyed) {
            this._markWidgetAsDestroyed = true;
            kendo.ui.DropDownList.fn.destroy.call(this);
            delete this._cachedParentWidget;
            this.markThisComponentAsClosed();

            this.unbind("change");
            this.unbind("open");
        }
    },

    // accepts a promise object.  Used to show a loading icon.  When this method is called, we show
    // the loader.  When the promise resolves, we hide the loader.
    loading: function(promise) {

        //This works by calling to internal methods on the dropdownlist
        //Unit tests will verify this continues to function on upgrade
        var me = this;
        me._showBusy({
            isDefaultPrevented: function() {
                return false
            }
        });
        $.when(promise).always(function() {
            me._hideBusy();
        });
    }
});

kendo.ui.plugin(DFDropDownList);
export default DFDropDownList;
